import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
  getDefaultValuesForForm,
  ButtonContainer,
} from '@product-framework/index';
import {
  Breadcrumb,
  FORM_ACTION,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { useNavigate, useParams } from 'react-router-dom';
import INSIGHTS_CONFIG_PATH from '../../apiPaths';
import { createSchema, editSchema } from '../../schemas';
import validationSchema from './validations';
import { FORM_STORE_NAME } from '../../store';
import {
  getInsightConfigDataById,
  preProcessRequestData,
  preProcessFetchData,
} from './lib';

const FORM_ID = 'MAINTENANCE_INSIGHT_CONFIG_FORM';

function InsightConfigMaintenance({ action = FORM_ACTION.CREATE }) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, put, get, getApiUrl } = useDataProvider(provider);
  const store = useStore();
  const { id: pageId = null } = useParams();

  const postApiUrl = `${getApiUrl()}${INSIGHTS_CONFIG_PATH}`;
  const updateApiUrl = `${getApiUrl()}${INSIGHTS_CONFIG_PATH}/${pageId}`;

  const isEditForm = action === FORM_ACTION.EDIT;
  const schema = isEditForm ? editSchema : createSchema;

  const params = {
    post,
    get,
    getApiUrl,
    formatMessage,
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      if (isEditForm) {
        return getInsightConfigDataById(params, pageId);
      }
      return {
        data: {
          ...getDefaultValuesForForm({
            schema,
          }),
        },
        errors: null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [isEditForm, pageId, schema, get, getApiUrl, formatMessage]);

  const onSubmitCreateForm = async data => {
    const requestData = preProcessRequestData(data);
    const response = await post(postApiUrl, { ...requestData });

    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'INSIGHT_CONFIG_CREATE_SUCCESS',
          defaultMessage: 'Insight config was created successfully',
        }),
      );
      return {
        nextValues: response?.data,
        errors: null,
        callback: () => navigate(`/insight-config/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmitEditForm = async data => {
    const defaultValues = formRef.current?.getDefaultValues?.() ?? {};
    const payload = {
      ...data,
      name: data.name ?? defaultValues.name,
      id: data.id ?? pageId,
    };
    const requestData = preProcessRequestData(payload);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'INSIGHT_CONFIG_UPDATE_SUCCESS',
          defaultMessage: 'Insight config was saved successfully',
        }),
      );
      return {
        nextValues: preProcessFetchData(response?.data),
        errors: null,
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmit = async data => {
    try {
      if (isEditForm) {
        return onSubmitEditForm(data);
      }
      return onSubmitCreateForm(data);
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'INSIGHT_CONFIG_BREADCRUMB',
        defaultMessage: 'Insight Config',
      }),
      icon: IconLibrary.System,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/insight-config');
      },
    },
    isEditForm
      ? {
          name: formatMessage({
            id: 'EDIT_INSIGHT_CONFIG_BREADCRUMB',
            defaultMessage: 'Edit Insight Config',
          }),
          icon: IconLibrary.System,
          onClick: undefined,
        }
      : {
          name: formatMessage({
            id: 'CREATE_INSIGHT_CONFIG_BREADCRUMB',
            defaultMessage: 'Create Insight Config',
          }),
          icon: IconLibrary.System,
          onClick: undefined,
        },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_INSIGHT_CONFIG_STACKABLE_BUTTON',
        defaultMessage: 'List Insight Config',
      }),
      Icon: IconLibrary.System,
      variant: 'outlined',
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/insight-config');
      },
    },
  ];

  if (isEditForm) {
    stackablePageButtons.unshift({
      label: formatMessage({
        id: 'CREATE_INSIGHT_CONFIG_STACKABLE_BUTTON',
        defaultMessage: 'Create Insight Config',
      }),
      Icon: IconLibrary.System,
      variant: 'outlined',
      onClick: () => navigate('/insight-config/create'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
    });
  }

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={
          isEditForm
            ? formatMessage({
                id: 'INSIGHT_CONFIG_EDIT_HEADING',
                defaultMessage: 'Edit Insight Config',
              })
            : formatMessage({
                id: 'INSIGHT_CONFIG_CREATE_HEADING',
                defaultMessage: 'Create Insight Config',
              })
        }>
        <Box>
          <Form
            submitWithReadOnlyFields
            permittedByDefault
            formId={FORM_ID}
            ref={formRef}
            onSubmit={onSubmit}
            schema={schema}
            validationSchema={validationSchema(formatMessage, isEditForm)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'INSIGHT_CONFIG_SAVE_BUTTON',
                  defaultMessage: 'Save',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'INSIGHT_CONFIG_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(InsightConfigMaintenance);
